import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Bracket from "@/components/manage/Bracket";
import ResultDialog from "@/components/manage/ResultDialog";
import { TeamAvatar, fmtTime } from "@/components/common";
import { teamLabel } from "@/components/manage/matchUtils";
import { Wand2, Trophy } from "lucide-react";

export default function ManageStructure({ data, refresh, teamMap }) {
  const { tournament: t, teams, matches, standings, group_standings } = data;
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${t.id}/generate`);
      toast.success("Structure & schedule generated");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Tournament structure</h2>
          <p className="text-sm text-muted-foreground">
            {t.tournament_type === "single_elimination" ? "Single elimination bracket" : t.tournament_type === "group_elimination" ? "Group stage + elimination" : "Round robin"} · {matches.length} matches
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={busy || teams.length < 2} data-testid="generate-structure-button">
              <Wand2 className="mr-1.5 h-4 w-4" /> {matches.length ? "Regenerate" : "Generate structure"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate tournament structure?</AlertDialogTitle>
              <AlertDialogDescription>
                This creates all matches and a proposed schedule based on current seeding.
                {matches.length > 0 && " Existing matches and any entered scores will be replaced."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={generate} data-testid="confirm-generate">Generate</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {teams.length < 2 && <Card className="p-8 text-center text-muted-foreground">Add at least 2 teams to generate a structure.</Card>}

      {matches.length > 0 && t.tournament_type === "single_elimination" && (
        <Card className="p-4">
          <Bracket matches={matches} teamMap={teamMap} onMatchClick={(m) => (m.team_a_id && m.team_b_id) && setActive(m)} />
        </Card>
      )}

      {matches.length > 0 && t.tournament_type === "round_robin" && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Standings</h3>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr><th className="p-2 text-left">Team</th><th className="p-2">P</th><th className="p-2">W</th><th className="p-2">D</th><th className="p-2">L</th><th className="p-2">+/-</th><th className="p-2">Pts</th></tr>
                </thead>
                <tbody>
                  {(standings || []).map((s, i) => (
                    <tr key={s.team_id} className="border-t" data-testid={`standing-row-${i}`}>
                      <td className="flex items-center gap-2 p-2"><span className="text-muted-foreground">{i + 1}</span><TeamAvatar team={{ name: s.name, color: s.color }} size={20} />{s.name}</td>
                      <td className="p-2 text-center">{s.played}</td><td className="p-2 text-center">{s.won}</td>
                      <td className="p-2 text-center">{s.drawn}</td><td className="p-2 text-center">{s.lost}</td>
                      <td className="p-2 text-center">{s.diff > 0 ? `+${s.diff}` : s.diff}</td>
                      <td className="p-2 text-center font-bold">{s.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Matches</h3>
            <div className="space-y-2">
              {matches.map((m) => (
                <Card key={m.id} className="card-lift cursor-pointer p-3" onClick={() => (m.team_a_id && m.team_b_id) && setActive(m)} data-testid={`rr-match-${m.id}`}>
                  <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>{m.round_name}</span><span>{m.status === "final" ? "FINAL" : fmtTime(m.scheduled_time, m.timezone)}</span></div>
                  <div className="flex items-center justify-between"><span>{teamLabel(m, "a", teamMap)}</span><span className="font-heading font-black">{m.score_a ?? ""}</span></div>
                  <div className="flex items-center justify-between"><span>{teamLabel(m, "b", teamMap)}</span><span className="font-heading font-black">{m.score_b ?? ""}</span></div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {matches.length > 0 && t.tournament_type === "group_elimination" && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Groups</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(group_standings || {}).map(([label, rows]) => (
                <Card key={label} className="overflow-hidden" data-testid={`group-standings-${label}`}>
                  <div className="bg-muted/50 px-3 py-2 text-label text-muted-foreground">Group {label}</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {rows.map((s, i) => (
                        <tr key={s.team_id} className={`border-t ${i < ((t.type_config || {}).qualifiers || 2) ? "bg-primary/5" : ""}`}>
                          <td className="flex items-center gap-2 p-2"><span className="text-muted-foreground">{i + 1}</span><TeamAvatar team={{ name: s.name, color: s.color }} size={18} /><span className="truncate">{s.name}</span></td>
                          <td className="p-2 text-center text-xs text-muted-foreground">{s.won}-{s.drawn}-{s.lost}</td>
                          <td className="p-2 text-center font-bold">{s.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Group matches</h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {matches.filter((m) => m.bracket === "group").map((m) => (
                <Card key={m.id} className="card-lift cursor-pointer p-3" onClick={() => (m.team_a_id && m.team_b_id) && setActive(m)} data-testid={`group-match-${m.id}`}>
                  <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>{m.round_name}</span><span>{m.status === "final" ? "FINAL" : fmtTime(m.scheduled_time, m.timezone)}</span></div>
                  <div className="flex justify-between"><span>{teamLabel(m, "a", teamMap)}</span><span className="font-heading font-black">{m.score_a ?? ""}</span></div>
                  <div className="flex justify-between"><span>{teamLabel(m, "b", teamMap)}</span><span className="font-heading font-black">{m.score_b ?? ""}</span></div>
                </Card>
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Elimination stage</h3>
            <Card className="p-4">
              <Bracket matches={matches.filter((m) => m.bracket === "main")} teamMap={teamMap} onMatchClick={(m) => (m.team_a_id && m.team_b_id) && setActive(m)} />
            </Card>
          </div>
        </div>
      )}

      <ResultDialog open={!!active} onOpenChange={() => setActive(null)} match={active} teamMap={teamMap} onDone={() => { refresh(); setActive(null); }} />
    </div>
  );
}
